import { FileUp, Plus } from "lucide-react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedParams } from "react-router-typesafe-routes";
import { DetailLoading, PanelLoading } from "~/components/loading";
import { Button } from "~/components/ui";
import { useAreaRecordGeoItems } from "~/features/recordGeoItems/hooks";
import { v2Route } from "~/v2/routes";
import { AreaGeoItemDetail } from "./AreaGeoItemDetail";
import { AreaGeoItemsList } from "./AreaGeoItemsList";
import { RecordGeoItemCreateWithFileDialog } from "./RecordGeoItemCreateWithFileDialog";

/**
 * V2 area view: a record's geo-items in a sidebar + a detail/map view of the selected one,
 * with create (form / H3-file upload / GeoTIFF overlay), edit, duplicate, and deactivate.
 */
export function AreaInputContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recordId = "" } = useTypedParams(v2Route.areaInput.record);
  const { geoItemId } = useTypedParams(v2Route.areaInput.record.detail);
  const { data } = useAreaRecordGeoItems(recordId);

  const record = data.records[0];
  const recordGeoItems = useMemo(() => record?.recordGeoItems ?? [], [record]);

  const firstActiveId = recordGeoItems.find((rgi) => !rgi.deactivatedAt)?.geoItemId ?? null;

  // Canonicalize the bare record URL onto the first active area so the selection is shareable
  // and bookmarkable (V1 parity). Only-deactivated records keep the bare URL and show NoData.
  useEffect(() => {
    if (!geoItemId && recordId && firstActiveId) {
      navigate(v2Route.areaInput.record.detail.$buildPath({ params: { recordId, geoItemId: firstActiveId } }), {
        replace: true,
      });
    }
  }, [geoItemId, recordId, firstActiveId, navigate]);

  const selectedGeoItemId = geoItemId ?? null;

  const onSelect = (nextGeoItemId: string) =>
    navigate(v2Route.areaInput.record.detail.$buildPath({ params: { recordId, geoItemId: nextGeoItemId } }));
  const [fileDialogOpen, setFileDialogOpen] = useState(false);

  if (!record) {
    return <div className="mx-auto max-w-[var(--content-max)] p-8 text-[13px] text-fg-3">{t("ui.notFound")}</div>;
  }

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-dewey-title text-fg-1">{record.nameJa || record.id}</h1>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setFileDialogOpen(true)}>
            <FileUp />
            <span>{t("feature.recordGeoItems.createWithH3IndexesFileDialog.openButton")}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(v2Route.areaInput.record.createWithOverlayImg.$buildPath({ params: { recordId } }))}
          >
            <Plus />
            <span>{t("feature.recordGeoItems.createWithOverlayImg.addButton")}</span>
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate(v2Route.areaInput.record.create.$buildPath({ params: { recordId } }))}
          >
            <Plus />
            <span>{t("ui.create")}</span>
          </Button>
        </div>
      </div>

      <RecordGeoItemCreateWithFileDialog open={fileDialogOpen} onOpenChange={setFileDialogOpen} recordId={recordId} />

      <div className="grid grid-cols-[220px_minmax(0,1fr)] gap-6">
        <aside className="min-w-0 border-r border-border-default pr-4">
          <AreaGeoItemsList recordGeoItems={recordGeoItems} selectedGeoItemId={selectedGeoItemId} onSelect={onSelect} />
        </aside>
        <main className="min-w-0">
          {selectedGeoItemId ? (
            <Suspense fallback={<PanelLoading />}>
              <AreaGeoItemDetail recordId={recordId} geoItemId={selectedGeoItemId} />
            </Suspense>
          ) : firstActiveId ? (
            // Redirect onto the first active area is in flight; avoid a NoData flash.
            <DetailLoading />
          ) : (
            <p className="text-[13px] text-fg-3">{t("feature.common.noData")}</p>
          )}
        </main>
      </div>
    </div>
  );
}
